import { defineStore } from 'pinia';
import { ref, Ref } from 'vue';
import axios from 'axios';
import type { Notifications, NotificationItem, Filters, Headers } from '@/types';
import createQueryString from '@/utils/queryString';
import { appendItems } from '@/utils/appendItems';
import { notificationsAdapter } from '@/adapters/adapters';

export const useNotificationsStore = defineStore('notifications', () => {
    const allowRequestFlag = ref<boolean>(false);
    const deleteNotificationFlag = ref<boolean>(false);
    const notifications = ref<Notifications>({
        items: [],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });

    const noViewedNotifications = ref<Notifications>({
        items: [],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const counterNewNotifications = ref<number>(0);
    function toSetAllowRequestFlag(value: boolean) {
        allowRequestFlag.value = value;
    }
    function toSetDeleteNotificationFlag(value: boolean) {
        deleteNotificationFlag.value = value;
    }
    function toAddNotification(notification: NotificationItem) {
        allowRequestFlag.value = true;
        // if (!noViewedNotifications.value.items.length) return;
        counterNewNotifications.value++;
        noViewedNotifications.value.items.unshift(notification);
    }
    const getHeaders = (contentType?: string): Headers => {
        const headers: { Authorization: string; 'Content-Type'?: string } = {
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
        };

        if (contentType) {
            headers['Content-Type'] = contentType;
        }

        return { headers };
    };

    const fetchData = async (url: string, headers: Headers) => {
        try {
            const response = await axios.get<Notifications>(url, headers);
            return response.data;
        } catch (error) {
            console.error(`Error fetching data from ${url}:`, error);
            throw error;
        }
    };

    const updateNotifications = (store: Ref<Notifications>, data: Notifications, page: number) => {
        if (page >= 2) {
            appendItems(store, data);
        } else {
            store.value = data;
        }
    };

    const awaitGetNotifications = async (filters: Filters, isScroll: boolean = false) => {
        const queryString = createQueryString(filters);
        try {
            const data = await fetchData(`/main-api/notifications/user-notifications${queryString}`, getHeaders());
            const updatedData = notificationsAdapter(data.items);
            const allData = {
                items: updatedData,
                total: data.total,
                size: data.size,
                page: data.page,
                pages: data.pages
            };
            if (isScroll) {
                if (filters.is_viewed === false) {
                    updateNotifications(noViewedNotifications, allData, filters.page);
                } else {
                    updateNotifications(notifications, allData, filters.page);
                }
            } else {
                notifications.value = allData;
            }
        } catch (error) {
            console.error(error);
        }
    };
    const awaitViewNotification = async (id: number) => {
        try {
            const response = await axios.put(`/main-api/notifications/user-notifications/${id}`, { is_viewed: true }, getHeaders('application/x-www-form-urlencoded'));
            if (response.status === 200) {
                noViewedNotifications.value.items = noViewedNotifications.value.items.filter((item) => item.id !== id);
            }
        } catch (error) {
            console.error(error);
        }
    };
    const awaitDeleteNotification = async (id: number) => {
        try {
            await axios.delete(`/main-api/notifications/user-notifications/${id}`, getHeaders());
            deleteNotificationFlag.value = true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    return {
        toSetDeleteNotificationFlag,
        deleteNotificationFlag,
        allowRequestFlag,
        toSetAllowRequestFlag,
        notifications,
        noViewedNotifications,
        toAddNotification,
        awaitGetNotifications,
        awaitViewNotification,
        awaitDeleteNotification,
        counterNewNotifications
    };
});
